import React, { useState } from 'react';
import { Button, Input, Label, Modal } from 'edozvola-frontend-lib/dist/ui';
import { useFormik } from 'formik';
import { Col, Collapse, FormGroup, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { CasesFilter, initialCasesFilter } from 'app/edozvola/cases/cases.model';
import { PaginatedDropdown } from 'edozvola-frontend-lib/dist/ui/components/paginated-dropdown';
import _ from 'lodash';
import { queryClient } from 'app/app';
import {
  fetchAdministrativeUnitForAuthorityQuery,
  fetchAuthorityCodebookQuery,
  fetchCadastralMunicipalitiesQuery,
  fetchCaseCategoryForFilterCodebookQuery,
  fetchCaseCategoryOptionsQuery,
  fetchCaseStatusForFilterQuery,
  fetchCommunitiesWithCityMunicipalitiesAndCountyQuery,
  fetchSubjectTypeQuery,
} from 'app/edozvola/codebooks/codebooks.queries';
import { casesFilterValidationSchema } from 'app/edozvola/cases/case-filter/model/case-filter.schema';
import { SUBJECT_TYPE_ENUM } from 'app/shared/enumeration/enums';
import { paginationKeys } from 'app/shared/util/pagination.constants';

export interface CasesFilterProps {
  filterForm: CasesFilter;
  setFilterForm: any;
  modal: any;
  closeModal: any;
  paginationKey: string;
}

const CasesFilterModal = (props: CasesFilterProps) => {
  const [caseFilterOpened, setCaseFilterOpened] = useState<boolean>(true);
  const [rpjLocationFilterOpened, setRpjLocationFilterOpened] = useState<boolean>(true);
  const [dkpLocationFilterOpened, setDkpLocationFilterOpened] = useState<boolean>(true);
  const [applicantFilterOpened, setApplicantFilterOpened] = useState<boolean>(true);

  const anyApplicantFieldIsNotEmpty = () => {
    return (
      formik.values.applicantOib ||
      formik.values.applicantMb ||
      formik.values.applicantFirstName ||
      formik.values.applicantLastName ||
      formik.values.applicantCompanyName
    );
  };

  const formik = useFormik({
    initialValues: props.filterForm,
    onSubmit: () => {},
    enableReinitialize: true,
    validateOnMount: true,
    validationSchema: casesFilterValidationSchema(anyApplicantFieldIsNotEmpty),
  });
  const { data: caseCategoryOptions, isLoading: caseCategoryOptionsLoading } = fetchCaseCategoryForFilterCodebookQuery();

  const { data: caseStatusOptions, isLoading: caseStatusOptionsLoading } = fetchCaseStatusForFilterQuery();
  const { data: authorityOptions, isLoading: authorityOptionsLoading } = fetchAuthorityCodebookQuery();
  const { data: administrativeUnitOptions, isLoading: administrativeUnitOptionsLoading } = fetchAdministrativeUnitForAuthorityQuery(formik.values.authority);
  const { data: communityOptions, isLoading: communityOptionsLoading } = fetchCommunitiesWithCityMunicipalitiesAndCountyQuery();
  const { data: cadastralMunicipalityOptions, isLoading: cadastralMunicipalityOptionsLoading } = fetchCadastralMunicipalitiesQuery();

  const { data: subjectTypeOptions, isLoading: subjectTypeOptionsLoading } = fetchSubjectTypeQuery();

  const handleSubmit = () => {
    props.setFilterForm(formik.values);
    localStorage.setItem(`${props.paginationKey}-filter-form`, JSON.stringify(formik.values));
    props.closeModal();
  };

  const clearFilter = () => {
    formik.resetForm({ values: initialCasesFilter });
  };

  const closeModal = () => {
    formik.setValues(props.filterForm);
    props.closeModal();
  };

  return (
    <Modal scrollable size={'xl'} backdrop="static" isOpen={!!props.modal} toggle={() => closeModal()} centered={true}>
      <ModalHeader close={<FontAwesomeIcon className={'hand'} icon={faXmark} />} onClick={e => closeModal()} className={'d-flex'}>
        Dodatni filteri
      </ModalHeader>
      <ModalBody>
        <Row className={'hand'} onClick={() => setCaseFilterOpened(!caseFilterOpened)}>
          <Col md={6}>
            <div className={'form-header'}>Predmet</div>
          </Col>
          <Col md={6} className={'d-flex justify-content-end'}>
            <FontAwesomeIcon size="lg" color="outline" icon={caseFilterOpened ? 'chevron-circle-up' : 'chevron-circle-down'} />
          </Col>
        </Row>
        <Collapse isOpen={caseFilterOpened}>
          <Row>
            <Col xs={4}>
              <FormGroup>
                <Label labelText={'Šifra predmeta'} />
                <Input
                  value={formik.values.webId}
                  onChange={event => formik.setFieldValue(`webId`, event.target.value)}
                  error={formik.errors.webId}
                />
              </FormGroup>
            </Col>
            <Col xs={4}>
              <FormGroup>
                <Label labelText={'Klasifikacijska oznaka'} />
                <Input
                  value={formik.values.classification}
                  onChange={event => formik.setFieldValue(`classification`, event.target.value)}
                  error={formik.errors.classification}
                />
              </FormGroup>
            </Col>
            <Col xs={4}>
              <FormGroup>
                <Label labelText={'Vrsta predmeta'} />
                <PaginatedDropdown
                  selected={formik.values.caseCategory}
                  labelKey={'value'}
                  multiple
                  onItemSelect={selectedOption => {
                    formik.setFieldValue('caseCategory', selectedOption);
                  }}
                  options={caseCategoryOptionsLoading ? [] : caseCategoryOptions}
                  placeholder={'Odaberi vrstu predmeta'}
                />
              </FormGroup>
            </Col>
            <Col xs={4}>
              <FormGroup>
                <Label labelText={'Status predmeta'} />
                <PaginatedDropdown
                  selected={formik.values.caseStatus}
                  labelKey={'value'}
                  clearButton
                  onItemSelect={selectedOption => {
                    formik.setFieldValue('caseStatus', selectedOption);
                    formik.setFieldValue('caseActivity', []);
                  }}
                  options={caseStatusOptionsLoading ? [] : caseStatusOptions}
                  placeholder={'Odaberi status predmeta'}
                />
              </FormGroup>
            </Col>
            <Col xs={4}>
              <FormGroup>
                <Label labelText={'Nadležno tijelo'} />
                <PaginatedDropdown
                  selected={formik.values.authority}
                  labelKey={'value'}
                  onItemSelect={selectedOption => {
                    formik.setFieldValue('authority', selectedOption);
                  }}
                  options={authorityOptionsLoading ? [] : authorityOptions}
                  placeholder={'Odaberi nadležno tijelo'}
                  clearButton
                />
              </FormGroup>
            </Col>
            <Col xs={4}>
              <FormGroup>
                <Label labelText={'Upravna jedinica'} />
                <PaginatedDropdown
                  selected={formik.values.administrativeUnit}
                  labelKey={'value'}
                  disabled={!formik.values.authority || formik.values.authority.length === 0}
                  multiple
                  onItemSelect={selectedOption => {
                    formik.setFieldValue('administrativeUnit', selectedOption);
                  }}
                  options={administrativeUnitOptionsLoading ? [] : administrativeUnitOptions}
                  placeholder={'Odaberi upravnu jedinicu'}
                />
              </FormGroup>
            </Col>
          </Row>
        </Collapse>

        <Row className={'hand'} onClick={() => setRpjLocationFilterOpened(!rpjLocationFilterOpened)}>
          <Col md={6}>
            <div className={'form-header'}>Lokacija zahvata (RPJ)</div>
          </Col>
          <Col md={6} className={'d-flex justify-content-end'}>
            <FontAwesomeIcon size="lg" color="outline" icon={rpjLocationFilterOpened ? 'chevron-circle-up' : 'chevron-circle-down'} />
          </Col>
        </Row>
        <Collapse isOpen={rpjLocationFilterOpened}>
          <Row>
            <Col xs={4}>
              <FormGroup>
                <Label labelText={'Naselje'} />
                <PaginatedDropdown
                  selected={formik.values.community}
                  labelKey={'value'}
                  multiple
                  onItemSelect={selectedOption => {
                    formik.setFieldValue('community', selectedOption);
                    formik.setFieldValue('street', []);
                    setTimeout(() => queryClient.invalidateQueries({ queryKey: ['codebook', 'streets'] }));
                  }}
                  options={communityOptionsLoading ? [] : communityOptions}
                  placeholder={'Odaberi naselje'}
                />
              </FormGroup>
            </Col>
          </Row>
        </Collapse>

        <Row className={'hand'} onClick={() => setDkpLocationFilterOpened(!dkpLocationFilterOpened)}>
          <Col md={6}>
            <div className={'form-header'}>Lokacija zahvata (DKP)</div>
          </Col>
          <Col md={6} className={'d-flex justify-content-end'}>
            <FontAwesomeIcon size="lg" color="outline" icon={dkpLocationFilterOpened ? 'chevron-circle-up' : 'chevron-circle-down'} />
          </Col>
        </Row>
        <Collapse isOpen={dkpLocationFilterOpened}>
          <Row>
            <Col xs={4}>
              <FormGroup>
                <Label labelText={'Katastarska općina'} />
                <PaginatedDropdown
                  selected={formik.values.cadastralMunicipality}
                  labelKey={'value'}
                  clearButton
                  onItemSelect={selectedOption => {
                    formik.setFieldValue('cadastralMunicipality', selectedOption);
                    formik.setFieldValue('cadastralParcel', '');
                  }}
                  options={cadastralMunicipalityOptionsLoading ? [] : cadastralMunicipalityOptions}
                  placeholder={'Odaberi katastarsku općinu'}
                />
              </FormGroup>
            </Col>
            <Col xs={4}>
              <FormGroup>
                <Label labelText={'Katastarska čestica'} />
                <Input
                  value={formik.values.cadastralParcel}
                  onChange={event => formik.setFieldValue(`cadastralParcel`, event.target.value)}
                  error={formik.errors.cadastralParcel}
                  disabled={!formik.values.cadastralMunicipality || formik.values.cadastralMunicipality?.length === 0}
                />
              </FormGroup>
            </Col>
          </Row>
        </Collapse>

        {props.paginationKey === paginationKeys.PARTICIPANT_CASES &&
          <>
            <Row className={'hand'} onClick={() => setApplicantFilterOpened(!applicantFilterOpened)}>
              <Col md={6}>
                <div className={'form-header'}>Podnositelj zahtjeva</div>
              </Col>
              <Col md={6} className={'d-flex justify-content-end'}>
                <FontAwesomeIcon size="lg" color="outline" icon={applicantFilterOpened ? 'chevron-circle-up' : 'chevron-circle-down'} />
              </Col>
            </Row>
            <Collapse isOpen={applicantFilterOpened}>
              <Row>
                <Col xs={4}>
                  <FormGroup>
                    <Label labelText={'Vrsta subjekta'} />
                    <PaginatedDropdown
                      selected={formik.values.applicantSubjectType}
                      labelKey={'value'}
                      clearButton
                      onItemSelect={selectedOption => {
                        formik.setFieldValue('applicantSubjectType', selectedOption);
                        formik.setFieldValue('applicantOib', '');
                        formik.setFieldValue('applicantMb', '');
                        formik.setFieldValue('applicantFirstName', '');
                        formik.setFieldValue('applicantLastName', '');
                        formik.setFieldValue('applicantCompanyName', '');

                        setTimeout(() => {
                          formik.validateForm();
                        }, 0);
                      }}
                      options={subjectTypeOptionsLoading ? [] : subjectTypeOptions}
                      placeholder={'Odaberi vrstu subjekta'}
                    />
                  </FormGroup>
                </Col>
                <Col xs={4}>
                  <FormGroup>
                    <Label labelText={'OIB'} />
                    <Input
                      maxLength={11}
                      value={formik.values.applicantOib}
                      onChange={event => {
                        formik.setFieldValue(`applicantOib`, event.target.value);
                        formik.setFieldValue('applicantFirstName', '');
                        formik.setFieldValue('applicantLastName', '');
                        formik.setFieldValue('applicantCompanyName', '');
                        setTimeout(() => {
                          formik.validateForm();
                        });
                      }}
                      disabled={
                        formik.values.applicantSubjectType?.length === 0 ||
                        ![SUBJECT_TYPE_ENUM.PRAVNA_OSOBA, SUBJECT_TYPE_ENUM.FIZICKA_OSOBA].includes(formik.values.applicantSubjectType[0].id) ||
                        !!formik.values.applicantFirstName ||
                        !!formik.values.applicantLastName ||
                        !!formik.values.applicantCompanyName
                      }
                      error={formik.errors.applicantOib}
                    />
                  </FormGroup>
                </Col>
                <Col xs={4}>
                  <FormGroup>
                    <Label labelText={'MB'} />
                    <Input
                      maxLength={11}
                      value={formik.values.applicantMb}
                      onChange={event => {
                        formik.setFieldValue(`applicantMb`, event.target.value);
                        formik.setFieldValue('applicantCompanyName', '');
                        setTimeout(() => {
                          formik.validateForm();
                        });
                      }}
                      disabled={
                        formik.values.applicantSubjectType?.length === 0 ||
                        ![SUBJECT_TYPE_ENUM.OBRT, SUBJECT_TYPE_ENUM.SLOBODNA_DJELATNOST, SUBJECT_TYPE_ENUM.OPG].includes(
                          formik.values.applicantSubjectType[0].id,
                        ) ||
                        !!formik.values.applicantCompanyName
                      }
                      error={formik.errors.applicantMb}
                    />
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col xs={4}>
                  <FormGroup>
                    <Label labelText={'Ime'} />
                    <Input
                      value={formik.values.applicantFirstName}
                      onChange={event => {
                        formik.setFieldValue(`applicantFirstName`, event.target.value);
                        setTimeout(() => {
                          formik.validateForm();
                        });
                      }}
                      disabled={
                        formik.values.applicantSubjectType?.length === 0 ||
                        formik.values.applicantSubjectType[0].id !== SUBJECT_TYPE_ENUM.FIZICKA_OSOBA ||
                        !!formik.values.applicantOib
                      }
                      error={formik.errors.applicantFirstName}
                    />
                  </FormGroup>
                </Col>
                <Col xs={4}>
                  <FormGroup>
                    <Label labelText={'Prezime'} />
                    <Input
                      value={formik.values.applicantLastName}
                      onChange={event => {
                        formik.setFieldValue(`applicantLastName`, event.target.value);
                        setTimeout(() => {
                          formik.validateForm();
                        });
                      }}
                      disabled={
                        formik.values.applicantSubjectType?.length === 0 ||
                        formik.values.applicantSubjectType[0].id !== SUBJECT_TYPE_ENUM.FIZICKA_OSOBA ||
                        !!formik.values.applicantOib
                      }
                      error={formik.errors.applicantLastName}
                    />
                  </FormGroup>
                </Col>
                <Col xs={4}>
                  <FormGroup>
                    <Label labelText={'Naziv'} />
                    <Input
                      value={formik.values.applicantCompanyName}
                      onChange={event => {
                        formik.setFieldValue(`applicantCompanyName`, event.target.value);
                        setTimeout(() => {
                          formik.validateForm();
                        });
                      }}
                      disabled={
                        formik.values.applicantSubjectType?.length === 0 ||
                        formik.values.applicantSubjectType[0].id === SUBJECT_TYPE_ENUM.FIZICKA_OSOBA ||
                        !!formik.values.applicantOib ||
                        !!formik.values.applicantMb
                      }
                      error={formik.errors.applicantCompanyName}
                    />
                  </FormGroup>
                </Col>
              </Row>
            </Collapse>
          </>
        }
      </ModalBody>
      <ModalFooter>
        <div>
          <Button
            color={'secondary'}
            size={'lg'}
            label={'Poništi'}
            prependIcon={'repeat'}
            onClick={clearFilter}
            disabled={_.isEqual(formik.values, initialCasesFilter)}
          />
        </div>
        <div className={'float-end'}>
          <Button
            color={'info'}
            size={'lg'}
            label={'Pretraži'}
            prependIcon={'search'}
            onClick={handleSubmit}
            disabled={!formik.isValid || _.isEqual(formik.values, props.filterForm)}
          />
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default CasesFilterModal;
